import React, { useState, useEffect, useCallback, useMemo, forwardRef } from 'react'
import { createPortal } from 'react-dom'
import {
  Form,
  Button,
  Row,
  Col,
  Card,
  Dropdown} from 'react-bootstrap'
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table'
import 'bootstrap/dist/css/bootstrap.min.css'
import {
  BsPersonPlusFill,
  BsFunnelFill,
  BsEyeFill,
  BsPencilFill,
  BsTrashFill,
  BsSearch,
  BsCalendar3,
  BsChevronDown,
  BsChevronUp,
  BsArrowClockwise,
  BsFileEarmarkSpreadsheetFill,
  BsFileEarmarkPdfFill,
  BsThreeDotsVertical,
  BsBookFill,
  BsArrowDown,
  BsArrowUp,
  BsArrowDownUp} from 'react-icons/bs'
import {
  LuGraduationCap} from 'react-icons/lu'
import ApiEndpoints,{BASE_URL, BASE_URL_Upload} from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import EditStudentModal from './EditStudentModal'
import { useLocation, useNavigate } from 'react-router-dom'
import SelectableField from './commonComponents/SelectableField'
import Cookies from 'js-cookie'
import axios from 'axios'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { useInstitute } from './commonComponents/InstituteContext'
import { excludePassedOutStudents } from '../utils/studentFilters'
import { NO_IMAGE_SRC } from '../utils/instituteBranding'

/** Renders dropdown menu on document.body so table overflow cannot clip it */
const PortalDropdownMenu = forwardRef(
  ({ children, style, className, show, close, align, ...props }, ref) =>
    createPortal(
      <div ref={ref} style={style} className={className} {...props}>
        {children}
      </div>,
      document.body,
    ),
)
PortalDropdownMenu.displayName = 'PortalDropdownMenu'

const actionMenuPopperConfig = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport',
        padding: 8,
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-end', 'bottom-end', 'top-start', 'bottom-start'],
        boundary: 'viewport',
        padding: 8,
      },
    },
    {
      name: 'offset',
      options: {
        offset: [0, 8],
      },
    },
  ],
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function fetchImageAsDataUrlForPdf(url) {
  if (!url || String(url).startsWith('data:')) return url

  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    /* fall through */
  }

  try {
    const proxiedUrl = `https://images.weserv.nl/?url=${encodeURIComponent(url)}`
    const res = await fetch(proxiedUrl)
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    /* fall through */
  }

  try {
    const proxiedUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`
    const res = await fetch(proxiedUrl)
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    /* ignore */
  }

  return null
}

/** Prefer the first non-empty roll value across API field name variants */
const pickRollNumber = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

const STUDENT_SORT_COLUMNS = [
  { key: 'admission_number', label: 'Admission Number' },
  { key: 'roll_number', label: 'Roll Number' },
  { key: 'name', label: 'Name' },
  { key: 'father_name', label: "Father's Name" },
  { key: 'admission_date', label: 'Admission Date' },
  { key: 'parents_number', label: "Parent's Number" },
]

const isEmptySortValue = (value) =>
  value === null || value === undefined || String(value).trim() === ''

const getStudentSortValue = (student, key) => {
  if (key === 'roll_number') {
    return pickRollNumber(student.roll_number, student.roll_no, student.rollNumber)
  }
  if (key === 'admission_date') {
    if (!student.admission_date) return null
    const time = new Date(student.admission_date).getTime()
    return Number.isNaN(time) ? null : time
  }
  const value = student?.[key]
  return value === null || value === undefined ? '' : String(value).trim()
}

/** Empty values stay at the bottom in both directions. */
const compareStudentRows = (a, b, key, direction) => {
  const valueA = getStudentSortValue(a, key)
  const valueB = getStudentSortValue(b, key)
  const emptyA = isEmptySortValue(valueA)
  const emptyB = isEmptySortValue(valueB)

  if (emptyA && emptyB) return 0
  if (emptyA) return 1
  if (emptyB) return -1

  let result = 0
  if (key === 'admission_date') {
    result = valueA - valueB
  } else {
    result = String(valueA).localeCompare(String(valueB), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  }

  if (result !== 0) {
    return direction === 'desc' ? -result : result
  }

  return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

async function makeWatermarkImageDataUrl(sourceDataUrl, size = 600, opacity = 0.1) {
  if (!sourceDataUrl) return null

  try {
    return await new Promise((resolve, reject) => {
      const imageEl = new Image()
      imageEl.crossOrigin = 'anonymous'
      imageEl.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = size
          canvas.height = size
          const ctx = canvas.getContext('2d')
          const minSide = Math.min(imageEl.naturalWidth, imageEl.naturalHeight)
          const sx = (imageEl.naturalWidth - minSide) / 2
          const sy = (imageEl.naturalHeight - minSide) / 2

          ctx.clearRect(0, 0, size, size)
          ctx.save()
          ctx.beginPath()
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
          ctx.closePath()
          ctx.clip()
          ctx.globalAlpha = opacity
          ctx.drawImage(imageEl, sx, sy, minSide, minSide, 0, 0, size, size)
          ctx.restore()

          resolve(canvas.toDataURL('image/png'))
        } catch (e) {
          reject(e)
        }
      }
      imageEl.onerror = reject
      imageEl.src = sourceDataUrl
    })
  } catch {
    return null
  }
}

const DataTable = () => {
  const {token, logout} = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  // const [instituteLogo, setInstituteLogo] = useState('no_image.png')
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id } = useInstitute();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        /**
         * Sidebar width:
         * - Expanded: 245px  (inline style)
         * - Collapsed: 85px (.sidebar-collapsed CSS with !important)
         * Add 1px gap between sidebar edge and dashboard content.
         */
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        // On mobile, sidebar becomes off-canvas, so no left offset is needed
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    // Set initial margin and mobile flag
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const [user, setUser] = useState(null)
  const [userRole, setUserRole] = useState([])
  const initialData = []

  const [data, setData] = useState(initialData)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [filteredData, setFilteredData] = useState([]) // Data displayed after filtering
  const [sortConfig, setSortConfig] = useState({
    key: 'roll_number',
    direction: 'asc',
  })
  const [loading, setLoading] = useState(false)
  const [resetKey, setResetKey] = useState(0)
  const [students, setStudents] = useState([])
  const [selectedClass, setSelectedClass] = useState('')
  const [showFilterCard, setShowFilterCard] = useState(false)
  const location = useLocation()
  const syncTableFromFilter = useCallback((rows) => {
    const list = Array.isArray(rows) ? rows : []
    setData(list)
    setFilteredData(list)
  }, [])

  useEffect(() => {
    const institueId = Cookies.get('institute_id')
    const userCookie = Cookies.get('user')    

    let token = null
    console.log('InstituteID', institueId)

    if (userCookie) {
      try {
        const user = JSON.parse(userCookie)
        console.log("userCookie",user)
        setUserRole(user)
        token = user.token
        console.log('TOKEN:', token)
        setUser(JSON.parse(userCookie))
      } catch (err) {
        console.error(' Failed to parse user cookie', err)
      }
    } else {
      console.warn('user cookie not found')
    }

    const fetchFilteredData = async () => {
      console.log('Insti ID: ', institueId)
      try {
        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'institute-Id': Number(institueId)}

        console.log('Request Headers:', headers)

        const response = await axios.get(
          BASE_URL+'/student/all',
          { headers },
        )

        const activeStudents = excludePassedOutStudents(response.data.data || [])
        console.log('Students response: ', activeStudents)
        setStudents(activeStudents)
        setData(activeStudents)
        setFilteredData(activeStudents)
      } catch (error) {
        toast.error(error.response?.data?.message || 'Something went wrong')
      }
    }

    fetchFilteredData()
  }, [loading])

  const [selectedData, setSelectedData] = useState({})
  const fetchFilteredData = ({
    course,
    class: classId,
    student,
    admission_date} = {}) => {
    console.log('COURSE:', course)
    console.log('class:', classId)
    console.log('student:', student)
    console.log('admission_date:', admission_date)
    
    // If no filters are applied, show all students
    if (!admission_date && !student && !course && !classId) {
      setData(students)
      setFilteredData(students)
      return
    }
    
    if (admission_date) {
      const selectedDate = new Date(admission_date).toISOString().split('T')[0]
      const filtered = students.filter((s) => {
        const studentDate = new Date(s.admission_date)
          .toISOString()
          .split('T')[0]
        return studentDate === selectedDate
      })
      setData(filtered)
      setFilteredData(filtered)
    }
    // if (student) {
    //   updatedData = updatedData.filter(
    //     (item) => item?.name?.toLowerCase().includes(student.toLowerCase()),
    //     console.log('student', student),
    //   )
    // }

    if (student) {
      console.log('STUDENT AFTER SELECT:', student)
      const studentName = students.find((s) => s.id === Number(student))
      let filtered = [...students]

      console.log('STUDENT NAME:', studentName)
      if (studentName) {
        // const updatedData = studentName
        //   .toLowerCase()
        //   .includes(studentName.toLowerCase())
        // console.log('updatedData', updatedData)
        setData([studentName])
      }

      const isNumeric = /^\d+$/.test(student) // Check if input is number (ID)

      if (isNumeric) {
        console.log('Filtering by student ID')
        filtered = filtered.filter((s) => s.id === Number(student))
      } else {
        console.log('Filtering by student NAME')
        filtered = filtered.filter((s) =>
          s.name?.toLowerCase().includes(student.toLowerCase()),
        )
      }
      console.log('Filtered Students:', filtered)
      setData(filtered)
      setFilteredData(filtered)
    }

    // Update the state with the filtered data
    // setFilteredStudents(updatedData)
  }

  useEffect(() => {
    let filteredStu = students
    console.log('🔵 Filtered Students:', filteredStu)

    if (selectedClass) {
      const instituteId = Cookies.get('institute_id')

      const headers = {
        'institute-Id': Number(instituteId),
        Authorization: user?.token ? `Bearer ${user.token}` : undefined}

      axios
        .get(
          BASE_URL+`/get/student/by-class?class_id=${selectedClass}`,
          { headers },
        )
        .then((res) => {
          const classStudents = res.data.data || []
          console.log('Fetched Students by class:', classStudents)

          const rollByStudentId = new Map(
            classStudents.map((stu) => [
              Number(stu.id),
              pickRollNumber(stu.roll_number, stu.roll_no, stu.rollNumber),
            ]),
          )
          const filteredData = excludePassedOutStudents(
            students
              .filter((student) => rollByStudentId.has(Number(student.id)))
              .map((student) => ({
                ...student,
                roll_number:
                  rollByStudentId.get(Number(student.id)) ||
                  pickRollNumber(
                    student.roll_number,
                    student.roll_no,
                    student.rollNumber,
                  ),
              })),
          )

          console.log('✅ Filtered Data to Display:', filteredData)
          setFilteredData(filteredData)
          setData(filteredData) // for table
        })
        .catch((error) => {
          // This is where 404 or other errors come
          if (
            error.response?.data?.message === 'No classes found for this course'
          ) {
            console.error('No classes found for this course.')
          } else {
            console.error('Error fetching classes:', error)
            console.error('Something went wrong while fetching classes.')
          }
          setFilteredData([])

          // setFilteredStudents([])
          setClasses([])
        })
    } else {
      // When no class is selected, show all students
      if (students.length > 0) {
        setFilteredData(students)
        setData(students)
      }
    }
  }, [students, selectedClass])

  const handleSelect = (selectedValues) => {
    setSelectedData(selectedValues)
    if (selectedValues.class) {
      const classId = selectedValues.class
      setSelectedClass(classId)
    }

    fetchFilteredData(selectedValues)
    if (selectedData.abc === 'abc') {
      console.log('Handling abc case')
    }
  }

  useEffect(() => {
    fetchFilteredData(selectedData)
  }, [selectedData])

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    )
    setCurrentPage(1)
  }

  const tableRows = useMemo(() => {
    const pageItems = filteredData.slice(indexOfFirstItem, indexOfLastItem)
    const source = data?.length > 0 ? data : pageItems
    const sortKey = isMobile ? 'roll_number' : sortConfig.key
    const sortDirection = isMobile ? 'asc' : sortConfig.direction
    return [...source].sort((a, b) =>
      compareStudentRows(a, b, sortKey, sortDirection),
    )
  }, [data, filteredData, indexOfFirstItem, indexOfLastItem, sortConfig, isMobile])

  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(1)
    }
  }, [filteredData, itemsPerPage, totalPages])

  const [courses, setCourses] = useState([])
  const [classes, setClasses] = useState([])
  const [searchAdmissionDate, setSearchAdmissionDate] = useState('')

  useEffect(() => {
    ApiEndpoints.getAllClasses().then((res) => setClasses(res.data.data))
    ApiEndpoints.getAllCourses().then((res) => setCourses(res.data.data))
  }, [])

  const handleView = (id) => {
    if (!id) {
      console.error('Student ID is undefined')
      return
    }
    console.log('Navigate to : ', `/student/profile/${id}`)
    navigate(`/student/profile/${id}`)
  }

  const handleEnrolledCourses = (id) => {
    if (!id) {
      console.error('Student id is undefined')
      return
    }

    const student = filteredData.find((item) => item.id === id)
    const studentName = student?.name
    const courseName =
      courses.find((course) => course.id === student?.course_id)?.name || 'N/A'
    const className =
      classes.find((cls) => cls.id === student?.class_id)?.class_name || 'N/A'

    console.log('Navigate to:', `/student/enrolled-courses/${id}`)
    console.log('Student Name:', studentName)
    console.log('Student ID:', id)
    console.log('Course Name:', courseName)
    console.log('Class Name:', className)
    console.log('Admission Number:', student?.admission_number)

    navigate(`/student/enrolled-courses/${id}`, {
      state: {
        student_name: studentName,
        student_id: id,
        course_name: courseName,
        class_name: className,
        admission_number: student?.admission_number}})
  }

  const handleUpdate = (data) => {
    console.log('Data:', data)
    navigate(`/student/update-profile/${data.id}`, {
      state: {
        data,
        status: data.status.charAt(0).toUpperCase() + data.status.slice(1)}})
    console.log('Data:', data)
  }
  /** Same row shape for Excel and PDF exports */
  const getStudentExportRows = () =>
    filteredData.map((student) => ({
      'Admission Number': student.admission_number,
      'Roll Number':
        pickRollNumber(
          student.roll_number,
          student.roll_no,
          student.rollNumber,
        ) || '-',
      Name: student.name,
      'Father Name': student.father_name,
      'Admission Date': student.admission_date,
      Course: courses.find((c) => c.id === student.course_id)?.name || 'N/A',
      Class:
        classes.find((c) => c.id === student.class_id)?.class_name || 'N/A',
      'Parent Number': student.parents_number}))

  const exportToExcel = () => {
    const exportData = getStudentExportRows()

    const worksheet = XLSX.utils.json_to_sheet(exportData)

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students')

    XLSX.writeFile(workbook, 'Student_Records.xlsx')
  }

  const exportToPdf = async () => {
    const exportData = getStudentExportRows()
    if (exportData.length === 0) {
      toast.error('No student records to export.')
      return
    }

    try {
      let watermarkDataUrl = null
      if (logo && logo !== NO_IMAGE_SRC) {
        try {
          const raw = await fetchImageAsDataUrlForPdf(logo)
          if (raw) {
            watermarkDataUrl = await makeWatermarkImageDataUrl(raw, 600, 0.1)
          }
        } catch {
          watermarkDataUrl = null
        }
      }

      const doc = new jsPDF('l', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 10
      const rowHeight = 8
      let yPos = 22

      const drawCenterWatermark = () => {
        if (!watermarkDataUrl) return
        const wmSize = Math.min(pageWidth, pageHeight) * 0.42
        const cx = (pageWidth - wmSize) / 2
        const cy = (pageHeight - wmSize) / 2
        doc.addImage(watermarkDataUrl, 'PNG', cx, cy, wmSize, wmSize)
      }

      drawCenterWatermark()

      const headers = [
        'Admission Number',
        'Roll Number',
        'Name',
        'Father Name',
        'Admission Date',
        'Course',
        'Class',
        'Parent Number',
      ]

      const colWidths = [32, 28, 36, 36, 28, 38, 28, 41]

      const drawTableHeader = () => {
        let xPos = margin
        doc.setFillColor(34, 56, 67)
        doc.rect(margin, yPos, pageWidth - margin * 2, rowHeight, 'F')
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(8)
        doc.setFont('helvetica', 'bold')
        headers.forEach((header, index) => {
          doc.text(header, xPos + 1.5, yPos + 5.5)
          xPos += colWidths[index]
        })
        yPos += rowHeight
      }

      const fitText = (text, maxLength) => {
        const safeText = String(text ?? '')
        if (safeText.length <= maxLength) return safeText
        return `${safeText.slice(0, maxLength - 3)}...`
      }

      const fitTextToColumn = (text, colIndex) => {
        const safeText = String(text ?? '')
        const maxWidth = colWidths[colIndex] - 3
        const lines = doc.splitTextToSize(safeText, maxWidth)
        return lines[0] || ''
      }

      const maxLens = [12, 18, 18, 18, 12, 20, 12, 16]

      const instituteDisplayName = String(
        name || Cookies.get('name') || Cookies.get('institute_name') || '',
      ).trim()

      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(34, 56, 67)
      doc.text(instituteDisplayName || 'Institute', pageWidth / 2, 10, {
        align: 'center'})

      doc.setFontSize(12)
      doc.text('Student Records', pageWidth / 2, 16, {
        align: 'center'})

      drawTableHeader()

      doc.setFont('helvetica', 'normal')
      doc.setTextColor(17, 24, 39)
      doc.setFontSize(8)

      exportData.forEach((rowObj, index) => {
        if (yPos + rowHeight > pageHeight - margin) {
          doc.addPage()
          drawCenterWatermark()
          yPos = 14
          drawTableHeader()
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(17, 24, 39)
          doc.setFontSize(8)
        }

        let xPos = margin
        const cells = headers.map((h, i) =>
          h === 'Roll Number'
            ? fitTextToColumn(rowObj[h], i)
            : fitText(rowObj[h], maxLens[i]),
        )

        if (index % 2 !== 0) {
          doc.setFillColor(249, 250, 251)
          doc.rect(margin, yPos, pageWidth - margin * 2, rowHeight, 'F')
        }

        cells.forEach((cell, colIndex) => {
          doc.text(String(cell), xPos + 1.5, yPos + 5.5)
          xPos += colWidths[colIndex]
        })

        yPos += rowHeight
      })

      doc.save('Student_Records.pdf')
    } catch (error) {
      console.error('Error while generating PDF:', error)
      toast.error('Failed to generate PDF.')
    }
  }
  // const handleRefresh = () => {
  //   setSelectedData({})
  //   setFilteredData(data) // Reset to original data
  //   setSearchAdmissionDate({})
  //   setSearch('')
  //   setSelectedClass(null)
  //   setData(students)
  //   setResetKey((prev) => prev + 1)
  // }
  const handleRefresh = () => {
    setSelectedData({}) // clears selected course/class/student
    setSearchAdmissionDate({})
    setSearch('')
    setSelectedClass('')
    setFilteredData(students)
    setData(students)
    setResetKey((prev) => prev + 1) // trigger reset in SelectableField
  }

  return (
    <>
      <style>
        {`
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }

          /* Allow 3-dots dropdown to render outside table/card */
          .table-card {
            overflow: visible !important;
            position: relative;
          }
          .table-wrapper {
            overflow-x: auto;
            overflow-y: visible !important;
            position: relative;
            max-height: none !important;
          }
          .table,
          .table tbody tr,
          .table td {
            overflow: visible !important;
          }
          .table td {
            position: relative;
          }
          .student-sort-header {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            width: 100%;
            background: transparent;
            border: none;
            padding: 16px;
            margin: 0;
            font: inherit;
            font-size: 13px;
            font-weight: 600;
            color: #111827;
            text-align: left;
            cursor: pointer;
            white-space: nowrap;
          }
          .student-sort-header:hover {
            color: #223843;
          }
          .student-sort-header:focus-visible {
            outline: 2px solid #223843;
            outline-offset: -2px;
          }
          .student-sort-header.is-active {
            color: #223843;
          }
          /* Portaled action menus (body) — keep above page chrome */
          body > .dropdown-menu {
            z-index: 2000 !important;
          }
      `}
      </style>
      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          marginRight: '0px',
          marginBottom: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px'}}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
        <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
          <div 
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              overflow: 'visible',
              borderBottom: '1px solid #D1D3D4',
              width: '100%'}}
          >
            
            {/* Left: Title and Add Student Icon Button */}
            <div className="d-flex align-items-center gap-3" style={{ flex: 1 }}>
              {/* School Logo */}
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                overflow: 'hidden', 
                background: '#223843',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0}}>
                {!logoError ? (
                  <img 
                    src={logo} 
                    alt="School Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>
              
              <h3 
                className="m-0 fw-bold" 
                style={{ 
                  letterSpacing: '-0.02em',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.2',
                  fontFamily: 'Inter, system-ui, sans-serif'}}
              >
                Active Students
              </h3>
              {/* <button
                onClick={() => navigate('/student/add')}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#223843 ',
                  border: 'none',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title="Add New Student"
              >
                <BsPersonPlusFill size={20} />
              </button> */}
            </div>
            
            {!isMobile && (
            <ProfileDropdown logout={logout} />
            )}
          </div>
        </div>
        </div>
        <div
          className="container-fluid p-0 mt-4"
          style={{
            // backgroundColor: '#F3F4F6',
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >
          {/* Filter Toggle Button and Items Per Page */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              {/* Items Per Page Selector */}
              <div 
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4'}}
              >
                <span style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#223843'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D3D4'
                  }}
                >
                  <option value="100">100 per page</option>
                  <option value="200">200 per page</option>
                  <option value="300">300 per page</option>
                </Form.Select>
              </div>
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>

            {/* Add New + export buttons at far-right (desktop); mobile copy below */}
            <div className="d-none d-md-flex align-items-center gap-2">
              <button
                type="button"
                className="d-flex align-items-center justify-content-center"
                onClick={() => navigate('/student/add')}
                style={{
                  borderRadius: '10px',
                  padding: '6px 10px',
                  height: '44px',
                  border: 'none',
                  backgroundColor: '#223843',
                  color: '#ffffff',
                  gap: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1f323a'
                  e.currentTarget.style.transform = 'scale(1.03)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title="Add New Student"
              >
                <BsPersonPlusFill size={18} />
                <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Add New Student
                </span>
              </button>
              {(user?.user_type === 'admin' || userRole?.roles?.includes("admin")) && (
                <>
                  <button
                    type="button"
                    onClick={exportToPdf}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#EF4444',
                      border: 'none',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'}}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#DC2626'
                      e.currentTarget.style.transform = 'scale(1.05)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#EF4444'
                      e.currentTarget.style.transform = 'scale(1)'
                    }}
                    title="Export PDF"
                  >
                    <BsFileEarmarkPdfFill size={20} />
                  </button>
                  <button
                    type="button"
                    onClick={exportToExcel}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#10B981',
                      border: 'none',
                      color: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'}}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#059669'
                      e.currentTarget.style.transform = 'scale(1.05)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#10B981'
                      e.currentTarget.style.transform = 'scale(1)'
                    }}
                    title="Export to Excel"
                  >
                    <BsFileEarmarkSpreadsheetFill size={20} />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="d-md-none d-flex justify-content-end align-items-center gap-2 mb-4">
            <button
              type="button"
              onClick={() => navigate('/student/add')}
              style={{
                borderRadius: '10px',
                padding: '6px 10px',
                height: '44px',
                border: 'none',
                backgroundColor: '#223843',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'}}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1f323a'
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#223843'
                e.currentTarget.style.transform = 'scale(1)'
              }}
              title="Add New Student"
            >
              <BsPersonPlusFill size={18} />
              <span style={{ fontSize: '12px', fontWeight: '650', fontFamily: 'Inter, system-ui, sans-serif' }}>
                Add New Student
              </span>
            </button>
            {(user?.user_type === 'admin' || userRole?.roles?.includes("admin")) && (
              <>
                <button
                  type="button"
                  onClick={exportToPdf}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'}}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#DC2626'
                    e.currentTarget.style.transform = 'scale(1.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#EF4444'
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                  title="Export PDF"
                >
                  <BsFileEarmarkPdfFill size={20} />
                </button>
                <button
                  type="button"
                  onClick={exportToExcel}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'}}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669'
                    e.currentTarget.style.transform = 'scale(1.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#10B981'
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                  title="Export to Excel"
                >
                  <BsFileEarmarkSpreadsheetFill size={20} />
                </button>
              </>
            )}
          </div>

          {/* Modern Filter Section */}
          <div 
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0'}}
          >
            {showFilterCard && (
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'}}
              >
                {/* Header */}
                <div 
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6'}}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'}}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search & Filter Students
                        </h5>
                        <p className="m-0 mt-1" style={{fontSize: '13px', color: '#6b7280' }}>
                          Find students quickly with advanced filters
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'}}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <Row className="g-4 text-start">
                    {/* SelectableField Filters */}
                    {(user?.user_type === 'admin' || userRole?.roles?.includes("admin")) && (
                      <Col xs={12}>
                        <div style={{ 
                          padding: '20px', 
                          backgroundColor: '#F3F4F6', 
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4'}}>
                          <div className="d-flex align-items-center gap-2 mb-3">
                            <div 
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                backgroundColor: '#223843',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'}}
                            >
                              <BsFunnelFill style={{ color: '#ffffff', fontSize: '16px' }} />
                            </div>
                            <label className="fw-semibold mb-0" style={{ color: '#111827', fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                              Filter by Course, Class & Student
                            </label>
                          </div>
                          <SelectableField
                            onSelect={handleSelect}
                            showCourse={true}
                            showClass={true}
                            showStudent={true}
                            abc={'abc'}
                            stripPassedOut
                            setData={syncTableFromFilter}
                            resetTrigger={resetKey}
                          />
                        </div>
                      </Col>
                    )}

                    {/* Filter by Student Name & Admission Date */}
                    <Col xs={12}>
                      <div style={{ 
                        padding: '20px', 
                        backgroundColor: '#F3F4F6', 
                        borderRadius: '8px',
                        border: '1px solid #D1D3D4'}}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div 
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#10B981',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'}}
                          >
                            <BsSearch style={{ color: '#ffffff', fontSize: '16px' }} />
                          </div>
                          <label className="fw-semibold mb-0" style={{ color: '#111827', fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                            Filter by Student Name & Admission Date
                          </label>
                        </div>
                        <Row className="g-3">
                          {/* Search Student Name */}
                          <Col xs={12} md={6}>
                            <div style={{ position: 'relative' }}>
                              <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                Search Student Name
                              </label>
                              <div style={{ position: 'relative' }}>
                                <BsSearch 
                                  style={{ 
                                    position: 'absolute', 
                                    left: '12px', 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    color: '#6b7280',
                                    fontSize: '16px',
                                    zIndex: 1}} 
                                />
                                <Form.Control
                                  type="text"
                                  value={search}
                                  onChange={(e) => {
                                    const value = e.target.value
                                    setSearch(value)
                                    const updatedSelection = { ...selectedData, student: value }
                                    console.log('Search input:', value)
                                    console.log('Updated Selection Object:', updatedSelection)
                                    setSelectedData(updatedSelection)
                                    fetchFilteredData(updatedSelection)
                                  }}
                                  placeholder="Enter student name..."
                                  style={{
                                    paddingLeft: '40px',
                                    borderRadius: '8px',
                                    border: '1px solid #D1D3D4',
                                    fontSize: '14px',
                                    height: '40px',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: '#ffffff',
                                    fontFamily: 'Inter, system-ui, sans-serif'}}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#223843'
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#D1D3D4'
                                  }}
                                />
                              </div>
                            </div>
                          </Col>

                          {/* Search by Admission Date */}
                          <Col xs={12} md={6}>
                            <div style={{ position: 'relative' }}>
                              <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                Search by Admission Date
                              </label>
                              <div style={{ position: 'relative' }}>
                                <BsCalendar3 
                                  style={{ 
                                    position: 'absolute', 
                                    left: '12px', 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    color: '#6b7280',
                                    fontSize: '16px',
                                    zIndex: 1,
                                    pointerEvents: 'none'
                                  }} 
                                />
                                <Form.Control
                                  type="date"
                                  value={searchAdmissionDate}
                                  onChange={(e) => {
                                    setSearchAdmissionDate(e.target.value)
                                    const newFilters = {
                                      ...selectedData,
                                      admission_date: e.target.value}
                                    setSelectedData(newFilters)
                                    fetchFilteredData(newFilters)
                                  }}
                                  placeholder="Select admission date"
                                  style={{
                                    paddingLeft: '40px',
                                    borderRadius: '8px',
                                    border: '1px solid #D1D3D4',
                                    fontSize: '14px',
                                    height: '40px',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: '#ffffff',
                                    fontFamily: 'Inter, system-ui, sans-serif'}}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#223843'
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#D1D3D4'
                                  }}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

        {/* Table */}
        <Card className="table-card" style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'visible', backgroundColor: '#ffffff', position: 'relative' }}>
          <div className="table-wrapper" style={{ overflowX: 'auto', overflowY: 'visible', position: 'relative', borderRadius: '12px' }}>
          <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'visible', position: 'relative', marginBottom: '0' }}>
            <Thead>
              <Tr style={{ backgroundColor: '#F3F4F6' }}>
                {STUDENT_SORT_COLUMNS.map((column) => {
                  const headerStyle = {
                    fontSize: '13px',
                    fontWeight: '600',
                    padding: isMobile ? '16px' : '0',
                    border: 'none',
                    borderBottom: '1px solid #D1D3D4',
                    textAlign: 'left',
                    color: '#111827',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }

                  if (isMobile) {
                    return (
                      <Th key={column.key} style={headerStyle}>
                        {column.label}
                      </Th>
                    )
                  }

                  const isActive = sortConfig.key === column.key
                  const SortIcon = !isActive
                    ? BsArrowDownUp
                    : sortConfig.direction === 'asc'
                      ? BsArrowUp
                      : BsArrowDown
                  return (
                    <Th
                      key={column.key}
                      aria-sort={
                        isActive
                          ? sortConfig.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                      style={headerStyle}
                    >
                      <button
                        type="button"
                        className={`student-sort-header${isActive ? ' is-active' : ''}`}
                        onClick={() => handleSort(column.key)}
                        title={
                          isActive && sortConfig.direction === 'asc'
                            ? `Sort ${column.label} descending`
                            : `Sort ${column.label} ascending`
                        }
                      >
                        <span>{column.label}</span>
                        <SortIcon size={12} aria-hidden="true" style={{ flexShrink: 0, opacity: isActive ? 1 : 0.45 }} />
                      </button>
                    </Th>
                  )
                })}
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {tableRows.length > 0 ? (
                tableRows.map((item, index) => (
                    <Tr 
                      key={item.id ?? index}
                      style={{ 
                        backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                        transition: 'background-color 0.2s ease',
                        borderBottom: '1px solid #E5E7EB'}}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                      }}
                    >
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.admission_number}</Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                        {pickRollNumber(item.roll_number, item.roll_no, item.rollNumber) || '-'}
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.name}</Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.father_name}</Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                        {item.admission_date
                          ? new Date(item.admission_date).toLocaleDateString(
                              'en-GB',
                              {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric'},
                            )
                          : 'N/A'}
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{item.parents_number}</Td>
                      <Td className="text-center" style={{ padding: '16px', border: 'none', position: 'relative' }}>
                        <Dropdown
                          align="end"
                          drop={
                            tableRows.length === 1
                              ? 'down'
                              : index >= tableRows.length - 2
                                ? 'up'
                                : 'down'
                          }
                        >
                          <Dropdown.Toggle
                            variant="light"
                            id={`dropdown-${item.id}`}
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              backgroundColor: 'transparent',
                              border: '1px solid #D1D3D4',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'}}
                            className="dropdown-toggle-no-caret"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#223843'
                              e.currentTarget.style.borderColor = '#223843'
                              e.currentTarget.style.color = '#ffffff'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent'
                              e.currentTarget.style.borderColor = '#D1D3D4'
                              e.currentTarget.style.color = '#111827'
                            }}
                          >
                            <BsThreeDotsVertical style={{ color: 'inherit' }} />
                          </Dropdown.Toggle>

                          <Dropdown.Menu
                            as={PortalDropdownMenu}
                            renderOnMount
                            popperConfig={actionMenuPopperConfig}
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              padding: '8px',
                              minWidth: '180px',
                              zIndex: 2000}}
                          >
                            {(user?.user_type === 'admin' || userRole?.roles?.includes("admin")) && (
                              <Dropdown.Item
                                onClick={() => handleView(item.id)}
                                style={{
                                  fontSize: '13px',
                                  padding: '10px 12px',
                                  color: '#111827',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  transition: 'all 0.2s ease',
                                  borderRadius: '6px',
                                  fontFamily: 'Inter, system-ui, sans-serif'}}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.color = '#ffffff'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent'
                                  e.currentTarget.style.color = '#111827'
                                }}
                              >
                                <BsEyeFill size={18} /> See Profile
                              </Dropdown.Item>
                            )}

                            {(user?.user_type === 'admin' || userRole?.roles?.includes("admin")) && (
                              <Dropdown.Item
                                onClick={() => handleUpdate(item)}
                                style={{
                                  fontSize: '13px',
                                  padding: '10px 12px',
                                  color: '#111827',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  transition: 'all 0.2s ease',
                                  borderRadius: '6px',
                                  fontFamily: 'Inter, system-ui, sans-serif'}}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#10B981'
                                  e.currentTarget.style.color = '#ffffff'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent'
                                  e.currentTarget.style.color = '#111827'
                                }}
                              >
                                <BsPencilFill size={18} /> Update Info
                              </Dropdown.Item>
                            )}

                            {!(user?.user_type === 'admin' || userRole?.roles?.includes('admin')) && (
                              <Dropdown.Item
                                onClick={() => handleView(item.id)}
                                style={{
                                  fontSize: '13px',
                                  padding: '10px 12px',
                                  color: '#111827',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  transition: 'all 0.2s ease',
                                  borderRadius: '6px',
                                  
                                  fontFamily: 'Inter, system-ui, sans-serif'}}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#223843'
                                  e.currentTarget.style.color = '#ffffff'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent'
                                  e.currentTarget.style.color = '#111827'
                                }}
                              >
                                <BsEyeFill size={18} /> View Profile
                              </Dropdown.Item>
                            )}

                            <Dropdown.Item
                              onClick={() => handleEnrolledCourses(item.id)}
                              style={{
                                fontSize: '13px',
                                padding: '10px 12px',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                borderRadius: '6px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#223843'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#111827'
                              }}
                            >
                              <BsBookFill size={18} /> Enrolled Courses
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </Td>
                    </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan="7" className="text-center" style={{ padding: '40px', fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    No records found
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
          </div>
        </Card>


        {/* Pagination & Total Records */}


                <TablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          totalItems={filteredData.length}
          itemLabel="students"
        />
        </div>

        <EditStudentModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          studentData={selectedCourse}
        />
      </div>
    </>
  )
}

export default DataTable
